import type { Account } from '@domain';

/**
 * Xero's account types, in words rather than in its shorthand.
 *
 * These headings ARE ours to translate, unlike the account names underneath
 * them. "DIRECTCOSTS" is Xero's own code for a category, not a client's label
 * for one of their accounts — so an Arabic interface should say تكلفة مباشرة
 * and not "Directcosts".
 *
 * The account names are a different matter entirely and are left exactly as
 * they arrive: they are the client's own words for their own accounts, and
 * rewriting them would be rewriting their books.
 *
 * Moved here out of the document form when the money screens needed the same
 * headings. Two copies of this list would drift, and the half that drifted
 * would be whichever screen somebody was not looking at.
 */
export const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  BANK: 'Bank',
  CURRENT: 'Current asset',
  CURRLIAB: 'Current liability',
  DEPRECIATN: 'Depreciation',
  DIRECTCOSTS: 'Direct costs',
  EQUITY: 'Equity',
  EXPENSE: 'Expense',
  FIXED: 'Fixed asset',
  INVENTORY: 'Inventory',
  LIABILITY: 'Liability',
  NONCURRENT: 'Non-current asset',
  OTHERINCOME: 'Other income',
  OVERHEADS: 'Overhead',
  PREPAYMENT: 'Prepayment',
  REVENUE: 'Revenue',
  SALES: 'Sales',
  TERMLIAB: 'Term liability',
  PAYGLIABILITY: 'Tax liability',
  SUPERANNUATIONEXPENSE: 'Pension expense',
  SUPERANNUATIONLIABILITY: 'Pension liability',
  WAGESEXPENSE: 'Wages',
  WAGESPAYABLELIABILITY: 'Wages payable',
  OTHER: 'Other',
};

/** The English heading for a Xero account type, ready to be translated. */
export function accountTypeLabel(type: string): string {
  return ACCOUNT_TYPE_LABELS[type || 'OTHER'] ?? ACCOUNT_TYPE_LABELS.OTHER!;
}

/**
 * Accounts grouped the way Xero's own screens group them.
 *
 * An accountant picking an account looks for it under a heading. A flat run of
 * several hundred codes is technically the same information and practically
 * unusable.
 */
export function groupAccounts(accounts: Account[]): { type: string; accounts: Account[] }[] {
  const groups = new Map<string, Account[]>();
  for (const account of accounts) {
    const key = account.type || 'OTHER';
    const list = groups.get(key) ?? [];
    list.push(account);
    groups.set(key, list);
  }
  return [...groups.entries()].map(([type, list]) => ({
    type,
    accounts: [...list].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true })),
  }));
}

/**
 * The accounts money can actually sit in.
 *
 * A transfer has nowhere else to go, and "paid from" on a spend is asking
 * which of the company's bank accounts the money left. Offering the whole
 * chart there invites somebody to spend money out of Sales.
 */
export function bankAccounts(accounts: Account[]): Account[] {
  return accounts
    .filter((account) => account.type === 'BANK')
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
}

/**
 * The accounts a line of spending or income can be filed against.
 *
 * Everything except the bank accounts: money moving from one bank account to
 * another is a transfer, which is a different object in Xero and a different
 * tab on the screen. Letting it be entered as a spend line would produce one
 * half of a transfer, with the other half missing.
 */
export function categoryAccounts(accounts: Account[]): Account[] {
  return accounts.filter((account) => account.type !== 'BANK');
}
